import type { LexicalNode } from 'lexical'
import { $createRangeSelection, ElementNode } from 'lexical'
import type {
    AdamContentItemNode,
    AdamLexicalNode,
} from '../../../../nodes/AdamLexicalNode'
import type { MoreOptionsOption } from '../../../../types/content-items'
import { $getAdjacentTextEditableNode } from '../../utils'

type AdamNoteNodePayload = {
    id?: string
}

class AdamNoteNode extends ElementNode {
    __payload: AdamNoteNodePayload

    static getFieldsToSanitize(): Record<string, unknown> {
        return {
            id: '',
        }
    }

    cleanForPaste() {
        this.__payload = {
            ...this.__payload,
            id: '',
        }
        this.getChildren().forEach((child) => {
            ;(child as AdamLexicalNode).cleanForPaste?.()
        })
    }

    getId(): string | null {
        return this.__payload.id ?? null
    }

    setId(id: string) {
        const self = this.getWritable()
        self.__payload.id = id
    }

    getEnabledOptions(): Record<MoreOptionsOption, boolean> {
        return {
            download: false,
            attachment: true,
            comment: true,
            tag: true,
            reaction: true,
            delete: true,
            duplicate: true,
            viewDetails: true,
            aiPrompts: true,
            integration: false,
            rowHeader: false,
            columnHeader: false,
            annotate: false,
            startPresentation: false,
        }
    }

    hasContent() {
        return this.getTextContentSize() !== 0
    }

    getContentType(): 'note' {
        return 'note'
    }

    isDraggable() {
        return false
    }

    canDropInside() {
        return false
    }

    isDropTarget() {
        return false
    }

    getContainerNode() {
        return this.getTopLevelElement()
    }

    collapseAtStartOffset(): boolean {
        console.log('[Editor] AdamNoteNode collapseAtStartOffset', this.__key)

        const newSelection = $createRangeSelection()
        newSelection.focus.set(this.getKey(), 0, 'element')

        const noteNode = $getAdjacentTextEditableNode(newSelection.focus, true)
        if (noteNode === null) return false

        const children = this.getChildren()
        noteNode.selectEnd()
        noteNode.append(...children)
        this.handleWhenContentRemoved()
        return true
    }

    collapseAtEndOffset(): boolean {
        console.log('[Editor] AdamNoteNode collapseAtEndOffset', this.__key)

        const newSelection = $createRangeSelection()
        newSelection.focus.set(this.getKey(), 0, 'element')

        const noteNode = $getAdjacentTextEditableNode(newSelection.focus, false)
        if (noteNode === null) return false

        const children = noteNode.getChildren()
        this.append(...children)
        noteNode.handleWhenContentRemoved()
        return true
    }

    handleWhenContentRemoved() {
        const containerNode =
            this.getContainerNode() as unknown as AdamContentItemNode
        this.remove()
        containerNode.handleWhenContentRemoved?.()
    }

    remove(preserveEmptyParent?: boolean): void {
        const parent = this.getTopLevelElement()
        super.remove(preserveEmptyParent)
        if (parent) parent.remove()
    }
}

function $isAdamNoteNode(
    node: LexicalNode | null | undefined,
): node is AdamNoteNode {
    return node instanceof AdamNoteNode
}

export { $isAdamNoteNode, AdamNoteNode }
